import { useEffect } from "react";
import { captureException } from "@sentry/browser";
import { AxiosRequestConfig } from "axios";
import useAxios, { configure, Options } from "axios-hooks";
import {
  setLatestClientVersion,
  setLatestEnvHash,
} from "utils/clientVersionTracker";

const ALLOWED_RESPONSE_CODES: Array<number | string | undefined> = [
  403,
  404,
  422,
  "403",
  "404",
  "422",
];

// Disable autoCanceling requests when useAxios config and options change.
// It's confusing and unexpected and we don't rely on it.
// https://github.com/simoneb/axios-hooks#refresh-behavior
configure({ defaultOptions: { autoCancel: false } });

export default function useRestApi<T = unknown>(
  config: AxiosRequestConfig,
  options?: Options,
) {
  config.headers || (config.headers = {});
  config.data || (config.data = {});

  // Axios pulls the csrf token for us
  config.xsrfCookieName = "CSRF-TOKEN";
  config.xsrfHeaderName = "X-CSRF-Token";
  const [response, refetch] = useAxios<T>(config, {
    // disable caching globally
    useCache: false,
    ...options,
  });

  useEffect(() => {
    const headers = response.response?.headers;

    const alongVersion = headers && headers["x-along-server-version"];
    if (alongVersion) {
      setLatestClientVersion(alongVersion);
    }

    const alongEnvHash = headers && headers["x-along-env-digest"];
    if (alongEnvHash) {
      setLatestEnvHash(alongEnvHash);
    }
  }, [response]);

  if (
    response.error &&
    !ALLOWED_RESPONSE_CODES.includes(response.error.response?.status) &&
    !response.error.response?.data.userError
  ) {
    captureException(response.error);
  }

  return [response, refetch] as const;
}
